import { useEffect, useState } from 'preact/hooks';
import type { AlbumManifest, MediaItem } from '@fdpf/shared';
import { api, ApiError } from '../lib/api.js';
import { Icon } from '../lib/Icon.jsx';
import { downloadUrl, mediaUrl } from './slideshow.js';

/**
 * A browsable list of everything in an album, with per-item save actions.
 *
 * The slideshow is the wrong place to save a specific photo: it advances on its own, so by the time
 * you have decided you want *this* one it has moved on. This page holds still.
 *
 * Two deliberately different actions per item, because on iOS they land in different places:
 *
 *   打开大图  → /m/<hash>/display in a new tab. That endpoint already serves a real `image/jpeg`
 *              with no Content-Disposition, so the browser renders it and a long-press offers
 *              "存储到照片" — the only route into the iOS Photos library that reliably works.
 *              Gives the 2560px copy, with EXIF GPS stripped. Photos only.
 *
 *   下载原文件 → /d/<hash>, Content-Disposition: attachment. Full-quality original, but on iOS it
 *              can only ever reach the Files app, never Photos.
 *
 * There is no web route that puts a VIDEO into the iOS Photos library. Long-pressing a `<video>`
 * offers no save action, and the share sheet's "Save Video" was observed failing silently. So the
 * video path is honest instead of clever: download to Files, then share from there. Confirmed on a
 * real device — an earlier share-sheet button looked like it worked and did nothing, which is worse
 * than telling the user the extra step.
 *
 * No new server endpoints were needed — both routes already existed and are covered by the smoke
 * tests.
 */

const mb = (bytes: number) => (bytes / 1024 / 1024).toFixed(bytes < 10 * 1024 * 1024 ? 1 : 0) + ' MB';

function when(item: MediaItem): string {
  const ms = item.takenAt ?? item.createdAt;
  if (!ms) return '';
  return new Date(ms).toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

function label(item: MediaItem, index: number): string {
  if (item.originalName) return item.originalName;
  return `${item.kind === 'video' ? '视频' : '照片'} ${index + 1}`;
}

export function AlbumList({ slug, onPlay, onBack }: { slug: string; onPlay(index: number): void; onBack(): void }) {
  const [manifest, setManifest] = useState<AlbumManifest | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [hint, setHint] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<AlbumManifest>(`/api/albums/${encodeURIComponent(slug)}`)
      .then(setManifest)
      .catch((err) => setError(err instanceof ApiError ? err.message : String(err)));
  }, [slug]);

  if (error) {
    return (
      <div class="center-note">
        <span class="note-icon">
          <Icon name="alert" size={40} />
        </span>
        <h1>打不开这个相簿</h1>
        <p>{error}</p>
        <p>
          <button class="btn-icon-label" onClick={onBack}>
            <Icon name="chevronLeft" size={17} />
            返回
          </button>
        </p>
      </div>
    );
  }

  if (!manifest) {
    return (
      <div class="center-note">
        <p>
          <span class="spinner" />
          正在加载…
        </p>
      </div>
    );
  }

  return (
    <div class="list-page">
      <div class="list-head">
        <button class="small btn-icon-label" onClick={onBack} aria-label="返回相簿列表">
          <Icon name="chevronLeft" size={16} />
          相簿
        </button>
        <span class="list-title">{manifest.album.title}</span>
        <button class="small primary btn-icon-label" onClick={() => onPlay(0)}>
          <Icon name="play" size={14} />
          播放
        </button>
      </div>

      <p class="hint">
        点缩略图可从那一张开始播放。
        <br />
        <strong>照片存进相册：</strong>点「打开大图」，然后长按图片 → 「存储到照片」。
        <br />
        <strong>视频存进相册：</strong>iOS 不允许网页直接存视频。点「下载原文件」存到「文件」App，
        再打开「文件」找到它 → 分享 → 「存储视频」。
        <br />
        「下载原文件」拿到的是完整原片（照片长按存下来的是缩小版）。
      </p>

      {hint && (
        <div class="list-hint">
          <Icon name="info" size={16} className="i-mid" /> {hint}
        </div>
      )}

      <div class="file-rows">
        {manifest.items.map((item, index) => (
          <div class="file-row" key={item.id}>
            <button
              class="file-thumb"
              onClick={() => onPlay(index)}
              aria-label={`从第 ${index + 1} 项开始播放`}
            >
              <img src={mediaUrl(item.contentHash, 'thumb')} alt="" loading="lazy" />
              {item.kind === 'video' && (
                <span class="file-badge">
                  <Icon name="play" size={9} />
                  {item.durationMs ? Math.round(item.durationMs / 1000) + 's' : ''}
                </span>
              )}
            </button>

            <div class="file-meta">
              <div class="file-name">{label(item, index)}</div>
              <div class="file-sub">
                {when(item)}
                {item.width && item.height ? ` · ${item.width}×${item.height}` : ''}
                {item.bytesDisplay ? ` · ${mb(item.bytesDisplay)}` : ''}
              </div>
            </div>

            <div class="file-actions">
              {/* Photos only: long-pressing a <video> offers no save action, so the button would lie. */}
              {item.kind === 'photo' && (
                <a
                  class="file-btn blue"
                  href={mediaUrl(item.contentHash, 'display')}
                  target="_blank"
                  rel="noopener"
                  onClick={() => setHint('新标签里长按图片 → 「存储到照片」，即可存进相册。')}
                >
                  <Icon name="image" size={16} />
                  打开大图
                </a>
              )}
              <a
                class="file-btn"
                href={downloadUrl(item.contentHash)}
                download={item.originalName ?? ''}
                onClick={() =>
                  setHint(
                    item.kind === 'video'
                      ? '视频已开始下载到「文件」App。要放进相册：打开「文件」→ 找到它 → 分享 → 「存储视频」。'
                      : '原文件已开始下载到「文件」App（不是相册）。要进相册请改用「打开大图」再长按。',
                  )
                }
              >
                <Icon name="download" size={16} />
                下载原文件
              </a>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
